import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Receipt,
  MapPin,
  Camera,
  X,
  CheckCircle,
  Check,
  Loader2,
  Banknote,
  Truck,
  Building2,
  QrCode,
  Zap,
  History,
  ArrowRight,
  Calendar,
} from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "sonner";
import api, { IMAGE_BASE_URL, ADMIN_IMAGE_URL } from "../api/Api"; // Adjust path as needed

// --- IMAGE COMPRESSION UTILITY ---
const compressImage = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 1024;
        const scaleSize = MAX_WIDTH / img.width;
        const newWidth = img.width > MAX_WIDTH ? MAX_WIDTH : img.width;
        const newHeight =
          img.width > MAX_WIDTH ? img.height * scaleSize : img.height;
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, newWidth, newHeight);
        ctx.canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Compression failed"));
            resolve(
              new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              }),
            );
          },
          "image/jpeg",
          0.6,
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const DriverOtherExpenses = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const qrInputRef = useRef(null);

  // --- UNIFIED FORM STATE ---
  const initialFormState = {
    trip_id: "",
    expense_id: "",
    expenseType: "",
    date: new Date().toISOString().split("T")[0],
    amount: "",
    location: "",
    latitude: null,
    longitude: null,
    notes: "",
    paymentMode: "office",
    expenseImg: null,
    expensePreview: null,
    paymentQrImg: null,
    paymentQrPreview: null,
  };

  const [form, setForm] = useState(initialFormState);

  // --- UI STATES ---
  const [activeTrips, setActiveTrips] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [fetchingLocation, setFetchingLocation] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- HISTORY STATES ---
  const [logs, setLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [fullImage, setFullImage] = useState(null);

  // --- DYNAMIC SETTINGS FROM DB ---
  const [dynamicSettings, setDynamicSettings] = useState({
    requireLocation: 0,
    requireImage: 0,
  });

  // --- 1. INITIAL DATA FETCHING ---
  useEffect(() => {
    fetchActiveTrips();
    fetchExpenseTypes();
    fetchHistory();
  }, []);

const formatDate = (dateString) => {
  if (!dateString) return "";
  
  const date = new Date(dateString);
  
  return date.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true 
  }); 
};


const fetchActiveTrips = async () => {
    const driverRegNo = localStorage.getItem("driverRegNo");
    if (!driverRegNo) return;

    setTripsLoading(true);
    try {
      const response = await api.get(
        `/driver/select-trips-expense?driver_regno=${driverRegNo}`
      );
      const tripData = response.data || [];
      const validTrips = tripData.filter((trip) => trip && trip.enq_id);
      setActiveTrips(validTrips);
      
    } catch (error) {
      console.error("Trip fetch error", error);
      setActiveTrips([]);
    } finally {
      setTripsLoading(false);
    }
  };

  const fetchExpenseTypes = async () => {
    try {
      // Fetch Dynamic Expense Types (where to_driver = 1)
      const res = await api.get("/expense/expense-types");
      if (res.data.success) {
        setExpenseTypes(res.data.data);
      }
    } catch (error) {
      console.error("Failed to load expense types", error);
    }
  };

  const fetchHistory = async () => {
    const driverRegNo = localStorage.getItem("driverRegNo");
    if (!driverRegNo) return;
    try {
      setHistoryLoading(true);
      // Calls the new history API route
      const response = await api.get(
        `/expense/otherexp-history/${driverRegNo}`,
      ); // Adjust to your actual route
      if (response.data.success) {
        setLogs(response.data.data);
      }
    } catch (error) {
      console.error("History fetch error", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // --- IMAGE URL HELPER ---
  const getImageUrl = (filename, type) => {
    if (!filename) return "https://placehold.co/400?text=No+Img";
    if (type === "payment_proof") {
      return `${ADMIN_IMAGE_URL}/uploads/expense_receipt/${filename}`;
    }
    return `${IMAGE_BASE_URL}/uploads/expense_receipt/${filename}`;
  };

  // --- INPUT HANDLERS ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleExpenseTypeChange = (e) => {
    const selectedId = e.target.value;
    const selectedOption = expenseTypes.find(
      (exp) => String(exp.expense_id) === String(selectedId),
    );

    setForm((prev) => ({
      ...prev,
      expense_id: selectedId,
      expenseType: selectedOption ? selectedOption.name : "",
    }));

    if (selectedOption) {
      setDynamicSettings({
        requireLocation: selectedOption.location,
        requireImage: selectedOption.image,
      });
    } else {
      setDynamicSettings({ requireLocation: 0, requireImage: 0 });
    }
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");

    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setForm((prev) => ({ ...prev, latitude, longitude }));

        const fetchAddress = async () => {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("TIMEOUT")), 5000),
          );
          const apiCall = api.post("/location/geocode", {
            latitude,
            longitude,
          });
          return Promise.race([apiCall, timeoutPromise]);
        };

        try {
          const res = await fetchAddress();
          if (res.data.success) {
            setForm((prev) => ({ ...prev, location: res.data.address }));
            toast.success("Expense Location found!");
          } else {
            setForm((prev) => ({
              ...prev,
              location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
            }));
            toast.warning("Address not found, using coords.");
          }
        } catch (error) {
          setForm((prev) => ({
            ...prev,
            location: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
          }));
          toast.success("GPS Coords captured.");
        } finally {
          setFetchingLocation(false);
        }
      },
      () => {
        toast.error("Unable to get location");
        setFetchingLocation(false);
      },
      { enableHighAccuracy: true },
    );
  };

  const handleImageUpload = async (e, fieldKey) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setCompressing(true);
      const compressed = await compressImage(file);
      setForm((prev) => ({
        ...prev,
        [`${fieldKey}Img`]: compressed,
        [`${fieldKey}Preview`]: URL.createObjectURL(compressed),
      }));
      toast.success("Photo captured successfully");
    } catch {
      toast.error("Photo processing failed");
    } finally {
      setCompressing(false);
    }
  };

  const removeImage = (fieldKey) => {
    setForm((prev) => ({
      ...prev,
      [`${fieldKey}Img`]: null,
      [`${fieldKey}Preview`]: null,
    }));
    if (fieldKey === "expense" && fileInputRef.current)
      fileInputRef.current.value = "";
    if (fieldKey === "paymentQr" && qrInputRef.current)
      qrInputRef.current.value = "";
  };

  // --- SUBMIT HANDLER ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.trip_id) return toast.error("Please select an Assigned Trip.");
    if (!form.expense_id)
      return toast.error("Please select an Expense Category.");
    if (!form.amount || form.amount <= 0)
      return toast.error("Please enter a valid Amount.");

    if (dynamicSettings.requireLocation === 1 && !form.location) {
      return toast.error("Location is mandatory for this expense type.");
    }
    if (dynamicSettings.requireImage === 1 && !form.expenseImg) {
      return toast.error(
        "Bill/Receipt photo is mandatory for this expense type.",
      );
    }

    setIsSubmitting(true);

    try {
      const driverId = localStorage.getItem("driverRegNo");
      const submitData = new FormData();

      submitData.append("driver_id", driverId);
      submitData.append("trip_id", form.trip_id);

      const selectedTrip = activeTrips.find(
        (trip) => String(trip.enq_id) === String(form.trip_id),
      );

      if (selectedTrip && selectedTrip.confirmation_id) {
        submitData.append("confirmation_id", selectedTrip.confirmation_id);
      }

      submitData.append("expense_id", form.expense_id);
      submitData.append("expense_type", form.expenseType);
      submitData.append("date", form.date);
      submitData.append("amount", form.amount);
      submitData.append("payment_mode", form.paymentMode);

      if (form.location) submitData.append("location", form.location);
      if (form.latitude) submitData.append("latitude", form.latitude);
      if (form.longitude) submitData.append("longitude", form.longitude);
      if (form.notes) submitData.append("notes", form.notes);

      if (form.expenseImg) {
        submitData.append("expense_receipt_slip", form.expenseImg);
      }

      if (form.paymentMode === "office" && form.paymentQrImg) {
        submitData.append("payment_qr_image", form.paymentQrImg);
      }

      const res = await api.post("/expense/add-other-expense", submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
       Swal.fire({
          icon: "success",
          title: "Expense Logged Successful",
          text:
            form.paymentMode === "office"
              ? "Sent to admin for approval."
              : "Cash expense auto-approved.",
          timer: 2500,
          showConfirmButton: false,
          width: "280px",
          padding: "1.25rem 1rem",
          customClass: {
            popup: "rounded-4 shadow-lg border-0",
            icon: "m-0 mx-auto",
            title: "fs-6 fw-bold mt-3 mb-1",
            htmlContainer: "m-0 small text-muted",
          },
          backdrop: "rgba(0,0,0,0.6)",
        });

        // 1. Reset the form safely
        setForm(initialFormState);
        setDynamicSettings({ requireLocation: 0, requireImage: 0 });

        // 2. Fetch updated history list!
        fetchHistory();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to submit expense.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- RENDER ---
  return (
    <div className="bg-white font-sans w-100 min-vh-100 overflow-x-hidden pb-2">
      <div className="container-fluid px-1">
        {/* --- GRADIENT HERO BANNER --- */}
        <div
          className="hero-gradient rounded-2 p-4 text-white position-relative overflow-hidden shadow-sm d-flex align-items-start"
          style={{ height: "140px" }}
        >
          <h4
            className="fw-bolder mb-0 position-relative z-1 text-uppercase mt-1"
            style={{ letterSpacing: "0.5px" }}
          >
            OTHER EXPENSES
          </h4>
          <Banknote
            className="position-absolute text-white opacity-25"
            size={140}
            strokeWidth={1.5}
            style={{
              right: "-20px",
              top: "-25px",
              transform: "rotate(-15deg)",
            }}
          />
        </div>

        <form onSubmit={handleSubmit}>
          {/* --- OVERLAPPING CARD: TRIP & CATEGORY --- */}
          <div
            className="card border-0 shadow-sm rounded-4 mb-4 native-card position-relative z-2 mx-2"
            style={{ marginTop: "-55px" }}
          >
            <div className="card-body p-3 p-md-4">
              {/* Trip Selection */}
              <div
                className="d-flex align-items-center justify-content-center gap-2 mb-2 text-primary fw-bold"
                style={{ fontSize: "0.90rem" }}
              >
                <Truck size={16} strokeWidth={2.5} /> Select Assigned Trip{" "}
                <span className="text-danger">*</span>
              </div>
              <div className="mb-4">
            <select
                  className="form-select custom-select-input rounded-4 fw-bold text-center" 
                  name="trip_id"
                  value={form.trip_id}
                  onChange={handleChange}
                  disabled={tripsLoading}
                  style={{ fontSize: "14px" }} 
                >
                  <option value="" style={{ color: "#333" }}>
                    {tripsLoading ? "Loading Trips..." : "-- Select Trip --"}
                  </option>
                  
                  {activeTrips.map((trip,index) => {
                    const optionColor = trip.trip_category === "Active" ? "#198754" : "#0d6efd";

                    return (
                      <option 
                        key={`${trip.enq_id}-${index}`} 
                        value={trip.enq_id}
                        style={{ color: optionColor, fontSize: "13px", fontWeight: "600" }} 
                        // disabled={trip.trip_category === "Old"}
                      >
                        {trip.confirmation_id || "Unknown"} 
                        
                        {` (${trip.trip_category}) `}
                        
                        {` | ${formatDate(trip.Pickup_time)} to ${formatDate(trip.dropoff_time)}`}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Category Selection */}
              <div
                className="d-flex align-items-center justify-content-center gap-2 mb-2 text-primary fw-bold"
                style={{ fontSize: "0.90rem" }}
              >
                <Receipt size={16} strokeWidth={2.5} /> Select Expense Type{" "}
                <span className="text-danger">*</span>
              </div>
              <div className="mb-2">
                <select
                  className="form-select custom-select-input rounded-4 fw-bold text-center text-dark"
                  name="expense_id"
                  value={form.expense_id}
                  onChange={handleExpenseTypeChange}
                >
                  <option value="">-- Select Type --</option>
                  {expenseTypes.map((exp) => (
                    <option key={exp.expense_id} value={exp.expense_id}>
                      {exp.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* --- DETAILS CARD (Location, Date, Amount) --- */}
          <div className="card border-0 shadow-sm rounded-4 mb-4 native-card mx-1">
            <div className="card-body p-3 p-md-4">
              {/* Location (Dynamic Required Asterisk) */}
              <div className="mb-4">
                <label
                  className="fw-bolder text-dark mb-2 text-uppercase"
                  style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
                >
                  LOCATION OF EXPENSE{" "}
                  {dynamicSettings.requireLocation === 1 && (
                    <span className="text-danger">*</span>
                  )}
                </label>
                <div className="d-flex gap-2">
                  <div className="bg-light rounded-4 d-flex align-items-center px-3 flex-grow-1 input-wrapper">
                    <input
                      type="text"
                      className="form-control bg-transparent border-0 px-0 shadow-none fw-medium text-dark"
                      placeholder="Expense Location..."
                      name="location"
                      value={form.location}
                      onChange={handleChange}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-warning text-dark fw-bold rounded-4 px-3 d-flex align-items-center gap-1 shadow-sm"
                    onClick={handleGetLocation}
                    disabled={fetchingLocation}
                    style={{ minWidth: "100px", justifyContent: "center" }}
                  >
                    {fetchingLocation ? (
                      <Loader2 size={16} className="spin" />
                    ) : (
                      <MapPin size={16} />
                    )}
                    Fetch
                  </button>
                </div>
              </div>

              {/* Amount & Date Grid */}
              <div className="row g-3 mb-2">
                <div className="col-6 text-center">
                  <label
                    className="fw-bolder text-dark mb-2 text-uppercase"
                    style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
                  >
                    DATE <span className="text-danger">*</span>
                  </label>
                  <div className="bg-light rounded-4 p-2 input-wrapper">
                    <input
                      type="date"
                      className="form-control bg-transparent border-0 text-center fw-bold text-dark shadow-none p-0 custom-date"
                      name="date"
                      value={form.date}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="col-6 text-center">
                  <label
                    className="fw-bolder text-teal mb-2 text-uppercase"
                    style={{
                      fontSize: "0.75rem",
                      letterSpacing: "0.5px",
                      color: "#0a9396",
                    }}
                  >
                    AMOUNT (₹) <span className="text-danger">*</span>
                  </label>
                  <div className="bg-teal-subtle rounded-4 p-2 input-wrapper-success border border-success-subtle">
                    <input
                      type="number"
                      className="form-control bg-transparent border-0 text-center fw-bold text-teal shadow-none p-0 amount-input no-spinners"
                      placeholder="0"
                      name="amount"
                      value={form.amount}
                      onChange={handleChange}
                      onWheel={(e) => e.target.blur()}
                      min="0"
                      step="0.01"
                      style={{ color: "#0a9396" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- PHOTOS CARD --- */}
          <div className="card border-0 shadow-sm rounded-4 mb-4 native-card mx-1">
            <div className="card-body p-3 p-md-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <label
                  className="fw-bolder text-dark text-uppercase mb-0"
                  style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
                >
                  BILL / RECEIPT / SLIP
                </label>
                {/* Dynamic Required Badge based on DB */}
                {dynamicSettings.requireImage === 1 ? (
                  <span
                    className="badge bg-danger-subtle text-danger rounded-pill px-2"
                    style={{ fontSize: "0.65rem" }}
                  >
                    Required
                  </span>
                ) : (
                  <span
                    className="badge bg-secondary-subtle text-secondary rounded-pill px-2"
                    style={{ fontSize: "0.65rem" }}
                  >
                    Optional
                  </span>
                )}
              </div>

              {!form.expensePreview ? (
                <div
                  className="upload-area rounded-4 d-flex flex-column align-items-center justify-content-center py-4 bg-light cursor-pointer"
                  onClick={() => !compressing && fileInputRef.current?.click()}
                >
                  <div className="bg-white p-3 rounded-circle shadow-sm mb-2 border">
                    {compressing ? (
                      <Loader2 size={26} className="text-primary spin" />
                    ) : (
                      <Camera size={26} className="text-secondary" />
                    )}
                  </div>
                  <span className="fw-bold text-dark small mt-1">
                    {compressing ? "Processing Image..." : "Tap to Capture"}
                  </span>
                </div>
              ) : (
                <div className="position-relative rounded-4 overflow-hidden border shadow-sm">
                  <img
                    src={form.expensePreview}
                    alt="Receipt Preview"
                    className="img-fluid w-100 object-fit-cover"
                    style={{ height: "200px" }}
                  />
                  <button
                    type="button"
                    className="btn btn-danger rounded-circle position-absolute p-1 d-flex align-items-center justify-content-center shadow"
                    style={{
                      top: "12px",
                      right: "12px",
                      width: "32px",
                      height: "32px",
                    }}
                    onClick={() => removeImage("expense")}
                  >
                    <X size={18} />
                  </button>
                  <div className="position-absolute bottom-0 start-0 w-100 bg-success p-2 text-white small d-flex align-items-center justify-content-center fw-bold gap-1">
                    <CheckCircle size={15} /> Uploaded Successfully
                  </div>
                </div>
              )}

              <input
                type="file"
                ref={fileInputRef}
                className="d-none"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleImageUpload(e, "expense")}
              />
            </div>
          </div>

          {/* --- NOTES / REMARKS CARD --- */}
          <div className="card border-0 shadow-sm rounded-4 mb-4 native-card mx-1">
            <div className="card-body p-3 p-md-4">
              <label
                className="fw-bolder text-dark mb-2 text-uppercase"
                style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
              >
                NOTES / REMARKS
              </label>
              <div className="input-wrapper bg-light rounded-4">
                <textarea
                  className="form-control bg-transparent border-0 rounded-4 fw-medium shadow-none p-3"
                  rows="3"
                  placeholder="Additional details..."
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                ></textarea>
              </div>
            </div>
          </div>

          {/* --- PAYMENT METHOD TOGGLE (OFFICE vs CASH) --- */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white mb-4 native-card mx-1">
            <div className="bg-light p-3 pb-2 border-bottom">
              <span
                className="fw-bolder text-dark text-uppercase"
                style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
              >
                PAYMENT METHOD
              </span>
            </div>
            <div className="p-3">
              <div className="row g-2">
                <div className="col-6">
                  <div
                    onClick={() => setForm({ ...form, paymentMode: "office" })}
                    className={`p-3 rounded-4 border text-center cursor-pointer transition-all native-btn d-flex flex-column align-items-center gap-1 ${form.paymentMode === "office" ? "bg-primary text-white border-primary shadow-sm" : "bg-white text-muted border-light-subtle"}`}
                  >
                    <Building2 size={24} />
                    <span className="fw-bold small">Submit to Office</span>
                  </div>
                </div>
                <div className="col-6">
                  <div
                    onClick={() => setForm({ ...form, paymentMode: "cash" })}
                    className={`p-3 rounded-4 border text-center cursor-pointer transition-all native-btn d-flex flex-column align-items-center gap-1 ${form.paymentMode === "cash" ? "bg-success text-white border-success shadow-sm" : "bg-white text-muted border-light-subtle"}`}
                  >
                    <Banknote size={24} />
                    <span className="fw-bold small">Pay Via Cash</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Conditional QR Upload for Office */}
            {form.paymentMode === "office" && (
              <div className="px-3 pb-4 animate-slide-down">
                <div
                  className="position-relative bg-light border border-2 border-dashed border-primary rounded-4 d-flex flex-column align-items-center justify-content-center cursor-pointer"
                  style={{ height: "160px" }}
                  onClick={() => qrInputRef.current.click()}
                >
                  <input
                    type="file"
                    hidden
                    ref={qrInputRef}
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "paymentQr")}
                  />
                  {form.paymentQrPreview ? (
                    <>
                      <img
                        src={form.paymentQrPreview}
                        className="w-100 h-100 object-fit-contain rounded-4 p-2"
                        alt="QR"
                      />
                      <div
                        className="position-absolute top-0 end-0 m-2 bg-dark text-white p-1 rounded-circle"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage("paymentQr");
                        }}
                      >
                        <X size={16} />
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-primary">
                      <div className="bg-white p-3 rounded-circle shadow-sm mb-2 d-inline-block border border-primary-subtle">
                        <QrCode size={28} />
                      </div>
                      <div className="fw-bold">Upload QR Code</div>
                      <div className="small opacity-75">Tap to upload</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* --- SUBMIT BUTTON --- */}
          <div className="mx-1 pb-2">
            <button
              type="submit"
              className={`btn w-100 rounded-pill py-3 fw-bold shadow-lg d-flex align-items-center justify-content-center gap-2 native-btn ${form.paymentMode === "office" ? "btn-primary" : "btn-success"}`}
              disabled={isSubmitting || compressing}
              style={{ fontSize: "1.05rem", letterSpacing: "0.5px" }}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </>
              ) : form.paymentMode === "office" ? (
                <>
                  {" "}
                  <Zap size={20} /> SUBMIT TO OFFICE{" "}
                </>
              ) : (
                <>
                  {" "}
                  <CheckCircle size={20} /> PAY VIA CASH{" "}
                </>
              )}
            </button>
          </div>
        </form>

        {/* ========================================================================================= */}
        {/* HISTORY LIST SECTION                                    */}
        {/* ========================================================================================= */}
        <div className="mt-5 mb-5 pb-5 mx-1">
          <div className="d-flex align-items-center justify-content-between mb-3 px-1 border-bottom pb-2">
            <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2 tracking-wide text-uppercase">
              <History size={18} className="text-primary" /> RECENT EXPENSES
            </h6>

            {/* LINK TO HISTORY PAGE */}
            <div
              onClick={() => navigate("/otherexp-history")} // Your full history route
              className="d-flex align-items-center gap-1 text-info fw-bold small cursor-pointer native-btn"
            >
              View All <ArrowRight size={16} />
            </div>
          </div>

          {historyLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary spinner-border-sm"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-5 text-muted opacity-50 small">
              No recent expenses found
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {/* SLICE TO SHOW ONLY 5 RECENT ITEMS */}
              {logs.slice(0, 5).map((log) => (
                <div
                  key={log.id}
                  onClick={() => setSelectedLog(log)}
                  className="card border-0 shadow-sm rounded-4 position-relative overflow-hidden native-btn cursor-pointer"
                >
                  <div
                    className={`position-absolute top-0 bottom-0 start-0 w-1 bg-${log.status_color}`}
                    style={{ width: "6px" }}
                  ></div>

                  <div className="p-3 ps-4 d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <div
                      className="d-flex align-items-center gap-3 flex-grow-1"
                      style={{ minWidth: 0 }}
                    >
                      <div
                        className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 bg-${log.status_color}-subtle text-${log.status_color}`}
                        style={{ width: "45px", height: "45px" }}
                      >
                        <Receipt size={20} />
                      </div>

                      <div style={{ minWidth: 0 }}>
                        <h6
                          className="fw-bold mb-0 text-dark text-truncate small text-uppercase"
                          style={{ maxWidth: "150px" }}
                        >
                          {log.expense_type}
                        </h6>

                        <small
                          className="text-dark d-flex align-items-center gap-1 mb-1"
                          style={{ fontSize: "0.75rem" }}
                        >
                          <Calendar size={12} />
                          {new Date(log.date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "2-digit",
                          })}
                        </small>

                        {/* Badges for Cash Payment and Location/Notes */}
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {Number(log.paid_thru) === 16 && (
                            <span
                              className="badge bg-info-subtle text-success border border-info-subtle"
                              style={{ fontSize: "0.65rem" }}
                            >
                              CASH PAYMENT
                            </span>
                          )}
                          {log.internal_notes &&
                            log.internal_notes !== "N/A" && (
                              <span
                                className="badge bg-info-subtle text-success border text-truncate"
                                style={{
                                  maxWidth: "100px",
                                  fontSize: "0.65rem",
                                }}
                              >
                                {/* <MapPin size={8} className="me-1" /> */}
                                {log.internal_notes}
                              </span>
                            )}
                        </div>
                      </div>
                    </div>

                    <div className="text-end flex-shrink-0">
                      <h6 className="fw-bold mb-1 text-dark">₹{log.amount}</h6>
                      <span
                        className={`badge rounded-pill bg-${log.status_color}-subtle text-${log.status_color} border border-${log.status_color}-subtle`}
                        style={{ fontSize: "0.65rem" }}
                      >
                        {log.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ============ TRANSACTION DETAILS MODAL ============ */}
        {selectedLog && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-end justify-content-center"
            style={{
              backgroundColor: "rgba(0,0,0,0.75)",
              zIndex: 9999,
              // backdropFilter: "blur(5px)",
              transform: "translateZ(0)", 
              WebkitTransform: "translateZ(0)"
            }}
            onClick={() => setSelectedLog(null)}
          >
            <div
              className="bg-white w-100 shadow-lg animate-slide-up"
              style={{
                maxWidth: "500px",
                maxHeight: "92%",
                overflowY: "auto",
                borderTopLeftRadius: "28px",
                borderTopRightRadius: "28px",
                position: "relative",
                zIndex: 10000, 
                transform: "translateZ(0)"
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Draggable Indicator */}
              <div
                className="d-flex justify-content-center pt-3 pb-2 cursor-pointer"
                onClick={() => setSelectedLog(null)}
              >
                <div
                  className="bg-secondary opacity-25 rounded-pill"
                  style={{ width: "45px", height: "5px" }}
                ></div>
              </div>

              {/* Header */}
              <div className="px-4 pb-3 d-flex justify-content-between align-items-center border-bottom border-light-subtle sticky-top bg-white">
                <div>
                  <h5 className="fw-bold mb-0 text-dark">Expense Details</h5>
                  <small
                    className="text-primary text-uppercase fw-semibold"
                    style={{ letterSpacing: "1px" }}
                  >
                    {selectedLog.expense_no}
                  </small>
                </div>
                <button
                  className="btn btn-light rounded-circle p-2 shadow-sm"
                  onClick={() => setSelectedLog(null)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-4 pb-4 pt-3">
                {/* Status Hero Card */}
                <div
                  className="p-4 rounded-4 mb-4 text-white shadow-sm position-relative overflow-hidden"
                  style={{
                    background:
                      "linear-gradient(135deg, #0cb1ca 0%, #0a9396 100%)",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start position-relative z-1">
                    <div>
                      <span className="opacity-75 small fw-bold text-uppercase">
                        Total Amount
                      </span>
                      <h1 className="display-6 fw-bold mb-0 mt-1">
                        ₹{selectedLog.amount}
                      </h1>
                    </div>
                    <span
                      className={`badge rounded-pill px-3 py-2 border bg-white text-${selectedLog.status_color} fw-bold`}
                    >
                      {selectedLog.status}
                    </span>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="bg-light rounded-4 p-3 mb-4 border border-light-subtle shadow-sm">
                  <div className="row g-3">
                    {/* row 1: Full Width */}
                    <div className="col-12 border-bottom pb-2">
                      <small
                        className="text-muted d-block fw-bold small text-uppercase mb-1"
                        style={{ fontSize: "0.65rem" }}
                      >
                        Expense Type
                      </small>
                      <span className="fw-bolder text-dark">
                        {selectedLog.expense_type}
                      </span>
                    </div>

                    {/* row 2: Split 50/50 */}
                    <div className="col-6 border-end">
                      <small
                        className="text-muted d-block fw-bold small text-uppercase mb-1"
                        style={{ fontSize: "0.65rem" }}
                      >
                        Location
                      </small>
                      <span
                        className="fw-bold text-dark d-block text-truncate"
                        title={selectedLog.location}
                      >
                        {selectedLog.location || "N/A"}
                      </span>
                    </div>

                    <div className="col-6 text-end ps-3">
                      <small
                        className="text-muted d-block fw-bold small text-uppercase mb-1"
                        style={{ fontSize: "0.65rem" }}
                      >
                        Date
                      </small>
                      <span className="fw-bold text-dark">
                        {new Date(selectedLog.date).toLocaleDateString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )}
                      </span>
                    </div>

                    {/* row 3: Full Width (For Note/Remarks) */}
                    <div className="col-12 border-top pt-2 mt-2">
                      <small
                        className="text-muted d-block fw-bold small text-uppercase mb-1"
                        style={{ fontSize: "0.65rem" }}
                      >
                        Driver Note
                      </small>
                      <p
                        className="mb-0 fw-medium text-dark small fst-italic"
                        style={{ lineHeight: "1.3" }}
                      >
                        {selectedLog.expense_note || "No notes provided."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* DRIVER UPLOADS */}
                <h6 className="fw-bold text-muted small mb-3 text-uppercase ls-1">
                  Driver Uploads
                </h6>
                <div className="row g-2 mb-4">
                  {[
                    { img: selectedLog.receipt_image, label: "RECEIPT / SLIP" },
                    { img: selectedLog.qr_image, label: "PAYMENT QR" },
                  ].map(
                    (item, idx) =>
                      item.img && (
                        <div className="col-6" key={idx}>
                          <div
                            className="ratio ratio-4x3 rounded-4 overflow-hidden border cursor-pointer bg-light position-relative shadow-sm"
                            onClick={() =>
                              setFullImage(getImageUrl(item.img, "driver"))
                            }
                          >
                            <img
                              src={getImageUrl(item.img, "driver")}
                              className="w-100 h-100 object-fit-cover"
                              alt={item.label}
                            />
                            <div
                              className="position-absolute bottom-0 w-100 bg-dark bg-opacity-50 text-white text-center py-1"
                              style={{ fontSize: "10px", fontWeight: "600" }}
                            >
                              {item.label}
                            </div>
                          </div>
                        </div>
                      ),
                  )}
                  {!selectedLog.receipt_image && !selectedLog.qr_image && (
                    <div className="col-12 text-center text-muted py-3 bg-light rounded-4 border border-dashed">
                      <p className="small mb-0 fw-medium">
                        No images uploaded.
                      </p>
                    </div>
                  )}
                </div>

                {/* PAYMENT VERIFICATION / ADMIN NOTES */}
                {(selectedLog.payment_proof_image ||
                  selectedLog.internal_notes) && (
                  <div className="mt-2 mb-4">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <h6 className="fw-bold mb-0 text-success small text-uppercase ls-1">
                        Payment Verification
                      </h6>
                    </div>

                    <div
                      className="p-3 rounded-4 border border-success-subtle bg-success"
                      style={{ "--bs-bg-opacity": ".03" }}
                    >
                      <div className="row g-3 align-items-center">
                        {/* Payment Proof Image */}
                        {selectedLog.payment_proof_image && (
                          <div className="col-4">
                            <div
                              className="ratio ratio-1x1 rounded-3 overflow-hidden border border-success border-opacity-25 cursor-pointer shadow-sm position-relative"
                              onClick={() =>
                                setFullImage(
                                  getImageUrl(
                                    selectedLog.payment_proof_image,
                                    "payment_proof",
                                  ),
                                )
                              }
                            >
                              <img
                                src={getImageUrl(
                                  selectedLog.payment_proof_image,
                                  "payment_proof",
                                )}
                                className="w-100 h-100 object-fit-cover"
                                alt="Payment Proof"
                              />
                              <div className="position-absolute top-0 end-0 bg-dark bg-opacity-50 text-white p-1 rounded-bottom-start">
                                <Check size={12} />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Admin Note Box */}
                        <div
                          className={
                            selectedLog.payment_proof_image ? "col-8" : "col-12"
                          }
                        >
                          <div className="d-flex flex-column gap-1">
                            <span
                              className="text-success small fw-bold text-uppercase"
                              style={{ fontSize: "10px" }}
                            >
                              Confirmation Comment
                            </span>
                            <div className="bg-white p-2 rounded-3 border border-success-subtle shadow-sm">
                              <p
                                className="mb-0 text-dark fw-bold small fst-italic"
                                style={{ lineHeight: "1.4" }}
                              >
                                {selectedLog.internal_notes || "Verified"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  className="btn btn-dark w-100 rounded-pill py-3 fw-bold mt-2 shadow native-btn"
                  onClick={() => setSelectedLog(null)}
                >
                  Close Details
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============ FULL SCREEN LIGHTBOX ============ */}
        {fullImage && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 bg-black d-flex align-items-center justify-content-center p-0"
            style={{ zIndex: 999999, 
              transform: "translateZ(0)", 
              WebkitTransform: "translateZ(0)" }}
            onClick={() => setFullImage(null)}
          >
            <button
              className="position-absolute top-0 end-0 m-4 btn btn-dark rounded-circle p-3 z-3 bg-opacity-50 border-0"
              onClick={() => setFullImage(null)}
            >
              <X size={24} className="text-white" />
            </button>
            <img
              src={fullImage}
              alt="Full View"
              className="img-fluid"
              style={{
                maxHeight: "100%",
                maxWidth: "100vw",
                objectFit: "contain",
              }}
            />
          </div>
        )}
      </div>
      {/* --- STYLES --- */}
      <style>{`
        .font-sans { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
        .cursor-pointer { cursor: pointer; }
        
        /* Gradient matching the screenshot style */
        .hero-gradient { 
          background: linear-gradient(135deg, #0cb1ca 0%, #68b04d 50%, #e28120 100%); 
        }

        /* Native Android Card Feel */
        .native-card {
          border: 1px solid rgba(0,0,0,0.05) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.03) !important;
        }

        /* Input borders matching modern mobile apps */
        .input-wrapper {
          border: 1.5px solid #e9ecef;
          transition: border-color 0.2s ease;
        }
        .input-wrapper:focus-within { border-color: #0cb1ca; }
        .input-wrapper-success { transition: border-color 0.2s ease; }

        .custom-select-input {
          background-color: #f8f9fa;
          height: 54px;
          border: 1.5px solid #e9ecef !important;
          box-shadow: none !important;
        }
        .custom-select-input:focus { border-color: #0cb1ca !important; }
        .form-control:focus { box-shadow: none !important; }

        /* Custom Teal color for Amount field */
        .bg-teal-subtle { background-color: #e6fcf5; }
        .text-teal { color: #0a9396 !important; }
        .amount-input::placeholder { color: rgba(10, 147, 150, 0.5); }

        /* FIX: Remove Number Input Spinners (Scroll Jacking fix) */
        .no-spinners::-webkit-outer-spin-button,
        .no-spinners::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"].no-spinners {
          -moz-appearance: textfield;
        }

        /* Hide calendar icon on iOS/Android for a cleaner look */
        .custom-date::-webkit-calendar-picker-indicator { 
          background: transparent; color: transparent; cursor: pointer;
          height: auto; left: 0; position: absolute; right: 0; top: 0; width: auto;
        }
        .custom-date { position: relative; }

        /* Upload Area Effects */
        .upload-area { border: 2px dashed #dee2e6; transition: all 0.2s ease; }
        .upload-area:active { background-color: #e9ecef !important; transform: scale(0.98); }

        /* General UI Effects & Animations */
        .native-btn:active { transform: scale(0.97); transition: transform 0.1s; }
        .transition-all { transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1); }
        .ls-1 { letter-spacing: 1px; }
        
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
        
        .animate-slide-down { animation: slideDown 0.3s ease-out; }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        
        /* Updated WebKit Animations for Android */
        .animate-slide-up { 
           animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
           -webkit-animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes slideUp { 
          from { 
            transform: translateY(100%); 
            -webkit-transform: translateY(100%);
          } 
          to { 
            transform: translateY(0); 
            -webkit-transform: translateY(0);
          } 
        }
        
        input:focus, select:focus, textarea:focus { outline: none; }
      `}</style>
    </div>
  );
};

export default DriverOtherExpenses;